import React, { PureComponent } from 'react';
import Text from '../../../components/Text.js';
import BubbleSelectBlock from '../../../components/BubbleSelectBlock.js';
import Slider from '../../../components/Slider.js';
import NumberInput from '../../../components/TextFields/NumberInput.js';
import { helpers } from "@simplybenefits/sb-utils";
import i18n from '../../../localization/i18n.js';
import tokens from '../../../localization/tokens.js';
class CoverageSelection extends PureComponent {
  componentDidMount() {
    this._handleCoverageChange(this.props.purchaseData.primary.volume);
  }

  /**
   * Retrieves the gross rate and premium based on the specified volume and entity type.
   *
   * @async
   * @function
   * @param {number} volume - The volume for rate calculation.
   * @param {string} entityType - The type of the entity for rate calculation.
   * @returns {Promise<Array<number>>} - Resolves with an array containing the gross rate and premium.
   * @private
   */
  _getPremiumAndRate = async (volume, entityType) => {
    const grossRate = await this.props.getRate(this.props.product, volume, this.props.member, entityType);
    // eslint-disable-next-line no-eval
    const premium = eval(this.props.product.calculations.premium);
    return [grossRate, premium];
  };

  /**
   * Handles the change in coverage by updating the volume, gross rate, and premium for the specified entity type.
   *
   * @async
   * @function
   * @param {number} volume - The new volume for coverage.
   * @param {string} [entityType='primary'] - The type of the entity for which coverage is being changed.
   * @returns {Promise<void>} - Resolves once the purchase data is successfully updated.
   */
  _handleCoverageChange = async (volume, entityType = 'primary') => {
    // get the new gross rate
    const [grossRate, premium] = await this._getPremiumAndRate(volume, entityType);
    const data = {
      ...this.props.purchaseData,
      [entityType]: {
        ...this.props.purchaseData[entityType],
        grossRate,
        volume,
        premium
      }
    };

    // if the product has auto volume setting for dependents (like CI for allstate, spouse gets 50% of primary volume) then set their volume and premiums based on the selected member volume and the volume multiplier
    if (this.props.product.dependentCoverageSetting.primaryVolumeMultiplier && entityType === 'primary') {
      if (this.props.purchaseData.dependent) {
        data.dependent.volume = volume * this.props.product.dependentCoverageSetting.primaryVolumeMultiplier;
        if (this.props.purchaseData.dependent.included) {
          const {
            dependentGrossRate,
            dependentPremium
          } = await this._getPremiumAndRate(data.primary.volume, 'dependent');
          data.dependent.grossRate = dependentGrossRate;
          data.dependent.premium = dependentPremium;
        }
      }
      if (this.props.purchaseData.spouse) {
        data.spouse.volume = volume * this.props.product.dependentCoverageSetting.primaryVolumeMultiplier;
        if (this.props.purchaseData.spouse.included) {
          const [spouseGrossRate, spousePremium] = await this._getPremiumAndRate(data.primary.volume, 'spouse');
          data.spouse.grossRate = spouseGrossRate;
          data.spouse.premium = spousePremium;
        }
      }
      if (this.props.purchaseData.child) {
        data.child.volume = volume * this.props.product.dependentCoverageSetting.primaryVolumeMultiplier;
        if (this.props.purchaseData.child) {
          const [childGrossRate, childPremium] = await this._getPremiumAndRate(data.primary.volume, 'child');
          data.child.grossRate = childGrossRate;
          data.child.premium = childPremium;
        }
      }
    }
    await this.props.savePurchaseData(data);
    return null;
  };

  /**
   * Handles changes in meta data for dependent entities, recalculating and updating relevant purchase data.
   *
   * @async
   * @function
   * @param {string} entityType - The type of the dependent entity.
   * @param {string} field - The field being updated in the dependent meta data.
   * @param {string|number|boolean} value - The new value for the specified field.
   * @returns {Promise<null>} - Resolves when the purchase data is updated.
   */
  _handleDependentMetaChange = async (entityType, field, value) => {
    if (this.props.purchaseData.spouse.included && value) {
      return null;
    }
    if (!this.props.purchaseData.spouse.included && !value) {
      return null;
    }

    // if we reduced the dependent volume amount based on the primaries coverage amount (opt CI) ensure we use the primary volume amount to calculate the dependents gross rate
    const volume = this.props.product.dependentCoverageSetting.primaryVolumeMultiplier ? this.props.purchaseData.primary.volume : this.props.purchaseData[entityType].volume;
    const grossRate = value ? await this.props.getRate(this.props.product, volume, this.props.member, entityType) : 0;
    // eslint-disable-next-line no-eval
    const premium = value ? eval(this.props.product.calculations.premium) : 0;
    const data = {
      ...this.props.purchaseData,
      [entityType]: {
        ...this.props.purchaseData[entityType],
        [field]: value,
        grossRate,
        premium
      }
    };
    await this.props.savePurchaseData(data);
    return null;
  };
  render() {
    const product = this.props.product;
    const name = `${product.type.toUpperCase() === 'VOLUNTARY' ? i18n.t(tokens.labels.marketplace.voluntary) : i18n.t(tokens.labels.marketplace.optional)} ${product.name.primary[this.props.member.language]}`;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      medium: true,
      margin: "0 0 10px 0"
    }, product.type.toUpperCase() === 'VOLUNTARY' ? i18n.t(tokens.labels.marketplace.selectCoverageAmountVoluntary) : i18n.t(tokens.labels.marketplace.selectCoverageAmountOptional)), /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      bold: true,
      flex: 1,
      margin: "20px 0 10px 0"
    }, i18n.t(tokens.labels.marketplace.selectCoverageAmountInfo)), /*#__PURE__*/React.createElement(Slider, {
      value: this.props.purchaseData.primary.volume,
      min: product.coverageRange.min,
      max: product.coverageRange.max,
      step: product.coverageRange.step,
      onBlur: value => this._handleCoverageChange(value)
    }), /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.marketplace.yourCoverageAmount),
      prefix: "$ ",
      value: this.props.purchaseData.primary.volume,
      disabled: true,
      required: true,
      variant: "outlined",
      customStyle: {
        marginRight: 1,
        width: 250
      }
    }), /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.marketplace.monthlyCost),
      prefix: "$ ",
      value: this.props.purchaseData.primary.premium,
      required: true,
      variant: "outlined",
      disabled: true,
      customStyle: {
        width: 250
      }
    }), product.verbiages.setCoverage.dependentCoverageNote1 && /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 0 0"
    }, product.verbiages.setCoverage.dependentCoverageNote1[this.props.member.language]), this.props.purchaseData.allowedDependentCoverage && /*#__PURE__*/React.createElement(React.Fragment, null, this.props.purchaseData.dependent && !this.props.coverageTypesBlocked.includes('dependent') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 0 0"
    }, i18n.t(tokens.labels.marketplace.purchaseDependentCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
      first: {
        title: i18n.t(tokens.buttons.yes),
        selected: this.props.purchaseData.dependent.included === true,
        field: 'yes'
      },
      second: {
        title: i18n.t(tokens.buttons.no),
        selected: this.props.purchaseData.dependent.included === false,
        field: 'no'
      },
      handleChange: field => this._handleDependentMetaChange('dependent', 'included', field === 'yes'),
      whitelabel: this.props.whitelabel
    }), this.props.purchaseData.dependent.included && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      medium: true,
      bold: true,
      margin: "10px 0 -10px 0"
    }, i18n.t(tokens.labels.marketplace.dependentCoverage)), /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 15px 0"
    }, product.verbiages.setCoverage.dependentCoverageNote2[this.props.member.language]), this.props.product.dependentCoverageSetting.allowed && /*#__PURE__*/React.createElement(Slider, {
      value: this.props.purchaseData.dependent.volume,
      min: product.coverageRange.min,
      max: product.coverageRange.max,
      step: product.coverageRange.step,
      onChange: value => this._handleCoverageChange(value, 'dependent')
    }), /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.marketplace.dependentCoverageAmount),
      prefix: "$ ",
      value: this.props.purchaseData.dependent.volume,
      variant: "outlined",
      customStyle: {
        marginRight: 10,
        width: 250
      },
      disabled: true,
      required: true
    }))), this.props.purchaseData.spouse && !this.props.coverageTypesBlocked.includes('spouse') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 0 0"
    }, i18n.t(tokens.labels.marketplace.purchaseSpouseCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
      first: {
        title: i18n.t(tokens.buttons.yes),
        selected: this.props.purchaseData.spouse.included === true,
        field: 'yes'
      },
      second: {
        title: i18n.t(tokens.buttons.no),
        selected: this.props.purchaseData.spouse.included === false,
        field: 'no'
      },
      handleChange: field => this._handleDependentMetaChange('spouse', 'included', field === 'yes'),
      whitelabel: this.props.whitelabel
    }), Boolean(this.props.purchaseData.spouse.included) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      medium: true,
      bold: true,
      margin: "10px 0 -10px 0"
    }, i18n.t(tokens.labels.marketplace.spouseCoverage)), /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 15px 0"
    }, product.verbiages.setCoverage.dependentCoverageNote2[this.props.member.language]), this.props.product.dependentCoverageSetting.allowed && /*#__PURE__*/React.createElement(Slider, {
      value: this.props.purchaseData.spouse.volume,
      min: this.props.product.dependentCoverageSetting.minSpouseCoverage || product.coverageRange.min,
      max: this.props.product.dependentCoverageSetting.maxSpouseCoverage || product.coverageRange.max,
      step: product.coverageRange.step,
      onChange: value => this._handleCoverageChange(value, 'spouse')
    }), /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.marketplace.spouseCoverageAmount),
      prefix: "$ ",
      value: this.props.purchaseData.spouse.volume,
      variant: "outlined",
      customStyle: {
        marginRight: 10,
        width: 250
      },
      disabled: true,
      required: true
    }))), this.props.purchaseData.child && !this.props.coverageTypesBlocked.includes('child') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 0 0"
    }, i18n.t(tokens.labels.marketplace.purchaseChildCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
      first: {
        title: i18n.t(tokens.buttons.yes),
        selected: this.props.purchaseData.child.included === true,
        field: 'yes'
      },
      second: {
        title: i18n.t(tokens.buttons.no),
        selected: this.props.purchaseData.child.included === false,
        field: 'no'
      },
      handleChange: field => this._handleDependentMetaChange('child', 'included', field === 'yes'),
      whitelabel: this.props.whitelabel
    }), this.props.purchaseData.child.included && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      medium: true,
      bold: true,
      margin: "10px 0 -10px 0"
    }, i18n.t(tokens.labels.marketplace.childCoverage)), /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      small: true,
      light: true,
      margin: "10px 0 15px 0"
    }, product.verbiages.setCoverage.dependentCoverageNote2[this.props.member.language]), this.props.product.dependentCoverageSetting.allowed && !this.props.product.dependentCoverageSetting.flatChildCoverage && /*#__PURE__*/React.createElement(Slider, {
      value: this.props.purchaseData.child.volume,
      min: product.coverageRange.min,
      max: product.coverageRange.max,
      step: product.coverageRange.step,
      onChange: value => this._handleCoverageChange(value, 'dependent')
    }), /*#__PURE__*/React.createElement(NumberInput, {
      label: i18n.t(tokens.labels.marketplace.childCoverageAmount),
      prefix: "$ ",
      value: this.props.product.dependentCoverageSetting.flatChildCoverage || this.props.purchaseData.child.volume,
      variant: "outlined",
      customStyle: {
        marginRight: 10,
        width: 250
      },
      disabled: true,
      required: true
    })))), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      mediumLarge: true,
      bold: true,
      margin: "20px 0 10px 0",
      customStyle: {
        flex: 1
      }
    }, i18n.t(tokens.labels.marketplace.productTotal, {
      product: name
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end'
      }
    }, /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      mediumLarge: true,
      bold: true,
      margin: "20px 0 10px 0"
    }, helpers.formatNumber(this.props.purchaseData.primary.premium + (this.props.purchaseData.child ? this.props.purchaseData.child.premium : 0) + (this.props.purchaseData.spouse ? this.props.purchaseData.spouse.premium : 0) + (this.props.purchaseData.dependent ? this.props.purchaseData.dependent.premium : 0), 2, true)), /*#__PURE__*/React.createElement(Text, {
      color: "#000",
      mediumLarge: true,
      light: true,
      margin: "20px 0 10px 0"
    }, i18n.t(tokens.labels.marketplace.perMonth)))));
  }
}
export default CoverageSelection;